import React, { useEffect } from 'react';

import {
  CTA,
  InputAppearanceValue,
  ThemeProvider,
} from '@vw-marketing/us-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { NoTranslateTextInput } from '../../../../shared/components/no-translate-text-input';
import {
  onVWBasicButtonClickWithProcessEvent,
  onVWBasicLayerLoadEvent,
} from '../../../../shared/tagging/tagging-helpers';

import { StyledButtonsContainer, StyledFormWrapper, StyledInputsContainer } from './styled';
import { UserData } from '../../../../shared/typings/main';

interface EditFullNameFormViewProps {
  authService: any;
  translationHandler: (key: string) => string;
  trackingManager: any;
  localeCode: string;
  updateUserProfile: (data: Partial<UserData>) => void;
  initialFirstName: string;
  initialLastName: string;
  onSuccess: () => void;
  onChange: () => void;
  isCanada: boolean;
}

export const EditFullNameFormView = ({
  authService,
  translationHandler,
  trackingManager,
  localeCode,
  updateUserProfile,
  initialFirstName,
  initialLastName,
  onSuccess,
  onChange,
  isCanada
}: EditFullNameFormViewProps) => {

  useEffect(() => {
    if (isCanada) {
      onVWBasicLayerLoadEvent(trackingManager, 'Edit Full Name');
    }
  }, []);

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required(translationHandler('accountSettings.edit.firstName.error.empty'))
      .matches(
        /^[A-Za-z\s]+$/,
        translationHandler('accountSettings.edit.firstName.error.invalid'),
      ),
    lastName: Yup.string()
      .required(translationHandler('accountSettings.edit.lastName.error.empty'))
      .matches(
        /^[A-Za-z\s]+$/,
        translationHandler('accountSettings.edit.lastName.error.invalid'),
      ),
  });

  const formik = useFormik({
    initialValues: {
      firstName: initialFirstName || '',
      lastName: initialLastName || '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        onVWBasicButtonClickWithProcessEvent(trackingManager, {
          Name: 'Account Settings',
          StepName: 'Personal Information and Security - Full Name',
          StepNumber: '4.1',
        }, { name: 'Update name' });
        const payloadObject: Record<string, any> = {
          firstName: values.firstName,
          lastName: values.lastName,
        };
        await authService.updateProfileDynamicGqlRequest(payloadObject, localeCode);
        updateUserProfile({ firstName: values.firstName, lastName: values.lastName });
        onSuccess();
      } catch (error) {
        console.error('Error updating user data:', error);
      }
    },
  });

  useEffect(() => {
    if (formik.dirty && onChange) {
      onChange();
    }
  }, [formik.values, onChange]);

  const getFieldProps = (
    name: string,
  ): {
    name: string;
    onChange: (e: React.SyntheticEvent<HTMLInputElement>) => void;
    onBlur: (e: React.SyntheticEvent<HTMLInputElement>) => void;
    value: any;
    message: string | undefined;
    appearance: InputAppearanceValue | undefined;
  } => ({
    name,
    onChange: (e) => {
      formik.handleChange(e);
    },
    onBlur: formik.handleBlur,
    value: formik.values[name],
    message:
      formik.touched[name] && formik.errors[name]
        ? formik.errors[name]
        : undefined,
    appearance:
      formik.touched[name] && formik.errors[name]
        ? ('error' as InputAppearanceValue)
        : undefined,
  });


  const isButtonDisabled = Object.keys(formik.errors).length > 0 || !formik.dirty || formik.isSubmitting;

  return (
    <ThemeProvider theme="main">
      <StyledFormWrapper>
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsContainer>
            <NoTranslateTextInput
              {...getFieldProps('firstName')}
              aria-label={translationHandler('registration.form.first.name.label')}
              aria-required="true"
              label={translationHandler('registration.form.first.name.label')}
              required
            />
            <NoTranslateTextInput
              {...getFieldProps('lastName')}
              aria-label={translationHandler('registration.form.last.name.label')}
              aria-required="true"
              label={translationHandler('registration.form.last.name.label')}
              required
            />
          </StyledInputsContainer>
          <StyledButtonsContainer>
            <CTA
              stretchContent
              aria-label="Save button"
              disabled={isButtonDisabled}
              emphasis="primary"
              tag="button"
              type="submit"
            >
              {translationHandler('accountSettings.edit.save')}
            </CTA>
          </StyledButtonsContainer>
        </form>
      </StyledFormWrapper>
    </ThemeProvider>
  );
};